import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect } from 'react';
import { TrainingOverviewStore, FolderEditDialogStore, TrainingEditDialogStore } from '../';
import { Tree } from 'primereact/tree';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { useState } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { Tooltip } from 'primereact/tooltip'; // Import Tooltip from PrimeReact
import SearchField from '../../../../components/SearchField';

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedSearch = sdk.currentStore?.searchValue || '';
    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});

const NodeTemplate = ({ sdk, node }: any) => {
    const isTraining = node.type === 'training';
    const isFolder = node.type === 'folder';
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            className="relative flex flex-col w-full gap-2 p-2 hover:bg-gray-100 rounded transition"
            style={{
                backgroundColor: isFolder ? '#f5f5f5' : 'transparent',
                flexWrap: 'wrap',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex items-center gap-3">
                {isFolder ? (
                    <i
                        className={`pi pi-folder text-xl text-gray-600`}
                        style={{
                            fontSize: '40px',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    ></i>
                ) : (
                    <img
                        src={'/schulung.svg'}
                        alt="Item Bild"
                        style={{ width: '28px', height: '28px', borderRadius: '10%' }}
                    />
                )}
                {node.img ? (
                    <img
                        src={node.img}
                        alt="Item Bild"
                        style={{ width: '32px', height: '32px', borderRadius: '10%' }}
                    />
                ) : null}
                {/* Node content */}
                <div className="flex-col flex-grow">
                    <div className="font-medium text-left">
                        {node.label}
                        <i
                            className={
                                isFolder
                                    ? `pi ${node.visible ? 'pi-eye' : 'pi-eye-slash'}`
                                    : `pi ${
                                          node.visible
                                              ? 'pi-check-circle' // Active
                                              : 'pi-times-circle' // Inactive
                                      }`
                            }
                            style={{
                                marginLeft: '8px',
                                color: isFolder
                                    ? 'gray' // Gray for folder visibility
                                    : node.visible
                                      ? 'green' // Green for active training
                                      : 'red', // Red for inactive training
                            }}
                        />
                    </div>
                    <div
                        className="text-sm text-gray-500 text-left max-w-xs break-words"
                        style={{ maxWidth: '600px' }}
                    >
                        {node.data || '-'}
                        {isTraining && node.visible && node.warning ? (
                            <>
                                <>
                                    <i
                                        id={`warning-icon-${node.id}`} // Unique ID for the tooltip target
                                        className="pi pi-exclamation-triangle text-lg ml-2"
                                        style={{
                                            color: '#f59e42', // Tailwind yellow-500 hex
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Tooltip
                                        target={`#warning-icon-${node.id}`} // Attach tooltip to the icon
                                        content="Die Schulung ist für Reinigungskräfte nicht sichtbar."
                                    />
                                </>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
            {isHovered && (
                <div
                    className="flex gap-2 p-1 shadow-md rounded transition"
                    style={{
                        zIndex: 99,
                        flexWrap: 'wrap',
                    }}
                >
                    {isFolder && (
                        <>
                            <Button
                                icon="pi pi-folder-plus"
                                className="p-button-sm p-button-text"
                                tooltip="Unterordner hinzufügen"
                                label="Neuer Ordner"
                                style={{
                                    fontSize: '0.8rem', // Kleinere Schriftgröße
                                    padding: '0.25rem 0.5rem', // Weniger Padding
                                }}
                                onClick={() => {
                                    sdk.openDialog(FolderEditDialogStore, {
                                        parent_id: node.id,
                                    });
                                }}
                            />
                            <Button
                                icon="pi pi-plus"
                                className="p-button-sm p-button-text"
                                tooltip="Schulung hinzufügen"
                                label="Neue Schulung"
                                style={{
                                    fontSize: '0.8rem', // Kleinere Schriftgröße
                                    padding: '0.25rem 0.5rem', // Weniger Padding
                                }}
                                onClick={() => {
                                    sdk.openDialog(TrainingEditDialogStore, {
                                        folder_id: node.id,
                                    });
                                }}
                            />
                        </>
                    )}
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-sm p-button-text"
                        tooltip="Bearbeiten"
                        style={{
                            fontSize: '0.8rem', // Kleinere Schriftgröße
                            padding: '0.25rem 0.5rem', // Weniger Padding
                        }}
                        label="Bearbeiten"
                        onClick={() => {
                            isTraining
                                ? sdk.openDialog(TrainingEditDialogStore, {
                                      training_id: node.id,
                                  })
                                : sdk.openDialog(FolderEditDialogStore, {
                                      folder_id: node.id,
                                  });
                        }}
                    />
                    {node.deleteable && (
                        <Button
                            icon="pi pi-trash"
                            className="p-button-sm p-button-text p-button-danger"
                            tooltip="Löschen"
                            style={{
                                fontSize: '0.8rem', // Kleinere Schriftgröße
                                padding: '0.25rem 0.5rem', // Weniger Padding
                            }}
                            label="Löschen"
                            onClick={async () => {
                                const result = await sdk.getDialogDelete({
                                    title: isTraining ? 'Schulung löschen' : 'Ordner löschen',
                                    message: isTraining
                                        ? `Möchten Sie die Schulung '${node.label}' wirklich löschen? Bitte geben Sie das Wort 'löschen' ein, um fortzufahren.`
                                        : `Möchten Sie den Ordner '${node.label}' wirklich löschen? Bitte geben Sie das Wort 'löschen' ein, um fortzufahren.`,
                                    searchWord: 'löschen',
                                    onConfirm: () =>
                                        isTraining
                                            ? sdk.currentStore.deleteTraining(node.id)
                                            : sdk.currentStore.deleteFolder(node.id),
                                });

                                if (result.confirmed) {
                                    sdk.showSuccessToast(
                                        isTraining ? 'Schulung gelöscht' : 'Ordner gelöscht'
                                    );
                                    sdk.currentStore.fetchTrainingFolderList();
                                } else if (result.error) {
                                    sdk.showErrorToast(result.error);
                                }
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const FolderTrainingTree = observer(({ sdk }: any) => {
    const store = sdk.currentStore;
    const handleDragDrop = (event: any) => {
        const { dragNode, dropNode } = event;

        const dragNodeType = dragNode?.type;
        const dropNodeType = dropNode?.type;

        if (dragNodeType && dropNodeType) {
            if (dropNodeType === 'training') {
                sdk.showErrorToast('Es kann nicht auf einen Training-Knoten abgelegt werden');
                return;
            }

            const newParentId = dropNode.id;
            const newSortOrder = (dropNode.children || []).map((child: any, i: number) => ({
                id: child.id,
                pos: i + 1,
                type: child.type,
            }));

            // Update the store with the new parent ID and sort order
            store.updateTreeSortOrder(
                dragNode.type === 'folder' ? dragNode.id : undefined,
                dragNode.type === 'training' ? dragNode.id : undefined,
                newParentId,
                newSortOrder
            );
        }
    };

    const nodeTemplate = (node: any) => {
        return (
            <NodeTemplate
                node={node}
                sdk={sdk}
            />
        );
    };

    return (
        <div className="p-4 max-w-2xl mx-auto">
            <Tree
                value={store.treeData}
                nodeTemplate={nodeTemplate}
                selectionMode="single"
                loading={store.isLoading}
                className="w-full"
                expandedKeys={store.treeExpanded}
                onToggle={e => store.setTreeExpanded(e.value)}
                dragdropScope="treeview"
                onDragDrop={handleDragDrop} // Callback für Drag-and-Drop
                header={() => <SearchToolbar sdk={sdk} />}
            />
        </div>
    );
});

const SearchToolbar = ({ sdk }: { sdk: ApplicationSdk }) => {
    return (
        <>
            <Toolbar
                start={() => null}
                center={() => null}
                end={() => (
                    <SearchField
                        store={sdk.currentStore}
                        placeholder={'Schulungen durchsuchen'}
                    />
                )}
            />
            <FilterChips sdk={sdk} />
        </>
    );
};

const TrainingOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(TrainingOverviewStore);
    }, [sdk]);

    return (
        <>
            <FolderTrainingTree sdk={sdk} />
        </>
    );
});

export default TrainingOverview;
